import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
  Input,
  FormControl,
  FormLabel,
  VStack,
  Text,
  Textarea,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { pxToRem } from "../../../utils/editor.ts";
import { useTranslation } from "react-i18next";
import ExportService, { 
  TemplateAdditionalInfo, 
  TemplateAdditionalInfoField,
  GetTemplateAdditionalInfoResponse 
} from "../../../services/exportServices.ts";

interface TemplateAdditionalInfoProps {
  paperId: string;
  templateId: number;
  onSaveSuccess?: () => void;
  onFieldsLoaded?: (hasFields: boolean) => void;
}

export interface TemplateAdditionalInfoRef {
  handleSave: () => Promise<boolean>;
  saving: boolean;
  hasFields: boolean;
}

export const TemplateAdditionalInfoPage = forwardRef<TemplateAdditionalInfoRef, TemplateAdditionalInfoProps>(({ 
  paperId, 
  templateId,
  onFieldsLoaded,
}, ref) => {
  const [additionalInfo, setAdditionalInfo] = useState<TemplateAdditionalInfo | null>(null);
  const [additionalInfoUuid, setAdditionalInfoUuid] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { t, i18n } = useTranslation();
  console.log(t('outline-text'))

  useEffect(() => {
    if (paperId && templateId) {
      fetchAdditionalInfo();
    }
  }, [paperId, templateId]);

  const fetchAdditionalInfo = async () => {
    setLoading(true);
    try {
      const response: GetTemplateAdditionalInfoResponse = await ExportService.getTemplateAdditionalInfo(paperId, templateId);
      setAdditionalInfo(response.data);
      if (!(response?.data && response.data?.fields && response?.data?.fields?.length > 0)) return
      setAdditionalInfoUuid(response.id);
      
      // 初始化表单数据
      const initialFormData: Record<string, string> = {};
      response.data.fields.forEach(field => {
        initialFormData[field.key] = field.value || field.default || "";
      });

      setFormData(initialFormData);
      
      // 通知父组件是否有字段需要填写
      if (onFieldsLoaded) {
        onFieldsLoaded(response.data.fields && response.data.fields.length > 0);
      }
    } catch (error) {
      console.error("Failed to fetch additional info:", error);
      toast({
        title: "Error",
        description: "Failed to load template additional information",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    
    // 清除该字段的错误状态
    if (fieldErrors[key]) {
      setFieldErrors(prev => ({
        ...prev,
        [key]: false
      }));
    }
  };

  const handleSave = async (): Promise<boolean> => {
    // 验证必填字段
    if (additionalInfo && additionalInfo.fields) {
      const requiredFields = additionalInfo.fields.filter(field => field.required);
      const emptyRequiredFields = requiredFields.filter(field => {
        const value = formData[field.key];
        return !value || value.trim() === '';
      });

      if (emptyRequiredFields.length > 0) {
        // 设置错误状态
        const newFieldErrors: Record<string, boolean> = {};
        emptyRequiredFields.forEach(field => {
          newFieldErrors[field.key] = true;
        });
        setFieldErrors(newFieldErrors);
        return false;
      }
    }

    setSaving(true);
    try {
      await ExportService.saveTemplateAdditionalInfo(additionalInfoUuid, formData);
      toast({
        title: "",
        description: "Template additional information saved successfully",
        status: "success",
        duration: 3000,
        position: "top"
      });

      return true;
    } catch (error) {
      console.error("Failed to save additional info:", error);
      toast({
        title: "Error",
        description: "Failed to save template additional information",
        status: "error",
        duration: 3000,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    handleSave,
    saving,
    hasFields: !!(additionalInfo && additionalInfo.fields && additionalInfo.fields.length > 0)
  }));

  const renderField = (field: TemplateAdditionalInfoField) => {
    const currentLang = i18n.language === 'zh' ? 'zh' : 'en';
    const label = field.label[currentLang] || field.label.en;
    const placeholder = field.placeholder[currentLang] || field.placeholder.en;
    const hasError = fieldErrors[field.key];

    return (
      <FormControl key={field.key} isRequired={field.required} isInvalid={hasError}>
        <FormLabel color={'#333'} fontSize={pxToRem(20)} mb={2}>
          {label}
        </FormLabel>
        {field.ui.widget === 'textarea' ? (
          <Textarea
            placeholder={placeholder}
            value={formData[field.key] || ""}
            borderColor={hasError ? '#E53E3E' : '#1D8AFF'}
            borderRadius={pxToRem(6)}
            _focus={{ borderColor: hasError ? '#E53E3E' : '#1D8AFF' }}
            _focusVisible={{ borderColor: hasError ? '#E53E3E' : '#1D8AFF' }}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            resize="vertical"
            minH={field.ui.size === 'small' ? pxToRem(80) : pxToRem(120)}
          />
        ) : (
          <Input
            placeholder={placeholder}
            value={formData[field.key] || ""}
            borderColor={hasError ? '#E53E3E' : '#1D8AFF'}
            borderRadius={pxToRem(6)}
            _focus={{ borderColor: hasError ? '#E53E3E' : '#1D8AFF' }}
            _focusVisible={{ borderColor: hasError ? '#E53E3E' : '#1D8AFF' }}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
          />
        )}
        {hasError && (
          <Text fontSize={pxToRem(20)} color="red.500" mt={1}>
            This field is required
          </Text>
        )}
        {field.description && !hasError && (
          <Text fontSize={pxToRem(20)} color="gray.500" mt={1}>
            {field.description}
          </Text>
        )}
      </FormControl>
    );
  };

  if (loading) {
    return (
      <VStack w={'full'} h={'full'} alignItems={'center'} justifyContent={'center'}>
        <Spinner size="lg" color="#1D8AFF" />
        <Text mt={4} color={'#8E9ABC'}>Loading Template Additional information...</Text>
      </VStack>
    );
  }

  if (!(additionalInfo && additionalInfo.fields && additionalInfo.fields.length > 0)) {
    return (
      <VStack w={'full'} h={'full'} alignItems={'center'} justifyContent={'center'}>
        <Text>The current template does not require any additional information to be entered.</Text>
      </VStack>
    );
  }

  return (
    <VStack w={'full'} h={'full'} alignItems={'start'} spacing={4} mt={2}>
      <Text fontWeight="bold" fontSize={pxToRem(20)} fontFamily={'Lato'} color="#333" mb={0}>
        Template Additional Information
      </Text>
      <Text fontSize={pxToRem(20)} fontFamily={'Lato'} color="#8E9ABC" mb={0}>
        Please fill in the required information for this template
      </Text>
      
      <VStack w={'full'} flex={1} spacing={4} align="start" overflowY="auto" pr={pxToRem(20)} maxH="400px">
        {additionalInfo.fields.map(field => renderField(field))}
      </VStack>
    </VStack>
  );
});

TemplateAdditionalInfoPage.displayName = 'TemplateAdditionalInfoPage';